'use client'

/**
 * CTA for free products (category free-magic-downloads, price 0).
 * Logged out: link to login with redirect back to product page.
 * Logged in: runs grantFreeDownload mutation, then redirects to /account/downloads with success banner.
 */
import { Button } from '@/General/Button'
import { useRouter } from 'next/navigation'
import { useStore } from '@/store'
import { useCookieValue } from '@/Cookies/useCookies'
import { toastEmitter } from '@/General/Toast'
import { tracking } from '@/tracking'
import { useGrantFreeDownloadMutation } from '@/generated/graphql'

interface FreeDownloadButtonProps {
  productSlug: string
  productName: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

export function FreeDownloadButton({
  productSlug,
  productName: _productName,
  className = 'w-full text-lg py-4 max-w-md',
  size = 'lg',
}: FreeDownloadButtonProps) {
  const router = useRouter()
  const user = useStore(state => state.user)
  const ecommBrandId = useStore(state => state.ecommBrandId)
  const brandCookie = useCookieValue('vi-brand')
  const brandId =
    ecommBrandId ?? (brandCookie === 'VANISHING_INC_UK' ? 2 : 1)

  const { mutate: grantFreeDownload, isPending } = useGrantFreeDownloadMutation({
    onSuccess: data => {
      const result = data.grantFreeDownload
      if (result.success && result.productName) {
        tracking.track('Registered for free download', {
          'Free download name': result.productName,
        })
      }
      const name = result.productName
        ? `&name=${encodeURIComponent(result.productName)}`
        : ''
      const already = result.alreadyHadAccess ? '&already=1' : ''
      router.push(`/account/downloads/?added=1${name}${already}`)
    },
    onError: (err: Error) => {
      toastEmitter.emit('toast', {
        title: err.message || 'Something went wrong. Please try again.',
        variant: 'error',
      })
    },
  })

  if (!user?.id) {
    return (
      <Button
        href={`/account/login?redirect=${encodeURIComponent(`/product/${productSlug}`)}`}
        color="green"
        size={size}
        className={className}
      >
        Download for free now
      </Button>
    )
  }

  return (
    <Button
      type="button"
      color="green"
      size={size}
      className={className}
      disabled={isPending}
      onClick={() =>
        user?.id &&
        grantFreeDownload({ customerId: user.id, brandId, productSlug })
      }
    >
      {isPending ? 'Downloading…' : 'Download for free now'}
    </Button>
  )
}
